import { CommonModule, registerLocaleData } from "@angular/common";
import localeFr from "@angular/common/locales/fr";
import { Component, HostListener, LOCALE_ID, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRouteSnapshot, NavigationEnd, Router, RouterOutlet } from "@angular/router";
import { Keepalive } from "@ng-idle/keepalive";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { MessageWait4signAlertComponent } from "@shared/components/message-wait4sign-alert/message-wait4sign-alert.component";
import { FRENCH_DAY_NAMES_MIN, FRENCH_MONTH_NAMES } from "@shared/constants/calendar.constants";
import { EN, FR } from "@shared/constants/languages.constants";
import { TOKEN } from "@shared/constants/localstorage.constants";
import { authGuard } from "@shared/guards/auth.guard";
import { ContactPhoneComponent } from "@shared/layout/contact-phone/contact-phone.component";
import { FooterComponent } from "@shared/layout/footer/footer.component";
import { HeaderComponent } from "@shared/layout/header/header.component";
import { AppInsightsService } from "@shared/services/application-insights.service";
import { AuthenticationService } from "@shared/services/authentication.service";
import { ConsultationContextService } from "@shared/services/consultation-context.service";
import { IdleUserService } from "@shared/services/idle-user.service";
import { ToasterMessageService } from "@shared/services/toaster-message.service";
import { Message, MessageService, PrimeNGConfig } from "primeng/api";
import { ToastModule } from "primeng/toast";
import { filter, Subject, takeUntil } from "rxjs";
import { AdvisorSessionExpiredService } from "@shared/services/advisor-session-expired.service";
import { PopupDialogComponent } from "@shared/components/popup-dialog/popup-dialog.component";

registerLocaleData(localeFr, "fr");

@Component({
  selector: "app-root",
  standalone: true,
  templateUrl: "./app.component.html",
  styles: "",
  imports: [
    CommonModule,
    RouterOutlet,
    FooterComponent,
    HeaderComponent,
    ToastModule,
    ContactPhoneComponent,
    TranslateModule,
    MessageWait4signAlertComponent,
    PopupDialogComponent,
  ],
  providers: [MessageService, Keepalive, { provide: LOCALE_ID, useValue: "fr-FR" }],
})
export class AppComponent implements OnInit, OnDestroy {
  title = "espace-adherents-website";
  isIdleWatchActive = false;
  browserRefresh = false;
  tabCloseDetected = false;
  showMessages = false;
  showAdvisorSessionExpiredDialog = false;

  advisorSessionExpiredMessage!: any;
  message!: Message[];
  private destroy$ = new Subject<void>();

  constructor(
    private appInsightsService: AppInsightsService,
    private authService: AuthenticationService,
    private idleService: IdleUserService,
    private messageService: MessageService,
    private translate: TranslateService,
    private primengConfig: PrimeNGConfig,
    private readonly toasterMessageService: ToasterMessageService,
    private readonly advisorSessionExpiredService: AdvisorSessionExpiredService,
    private router: Router,
    private readonly consultationContextService: ConsultationContextService
  ) {
    this.appInsightsService.startMonitoring();
    this.idleTimeout();
    this.initializeTranslation();
  }

  ngOnInit() {
    this.consultationContextService.captureFromUrlAndClean();
    this.listenToAdvisorSessionExpired();
    this.initI18n();
    this.toasterMessageService.message$.subscribe({
      next: message => {
        this.messageService.add({
          severity: message.severity,
          summary: message.summary,
          detail: message.detail,
          life: message.timeout,
        });
      },
    });
    this.checkAuthGuardRoute();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  checkAuthGuardRoute(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        const currentRoute = this.router.routerState.snapshot.root;
        this.showMessages = this.isProtectedRoute(currentRoute);
      });
  }

  isProtectedRoute(route: ActivatedRouteSnapshot): boolean {
    if (route.routeConfig && route.routeConfig.canActivate && route.routeConfig.canActivate.includes(authGuard)) {
      return true;
    }
    if (route.firstChild) {
      return this.isProtectedRoute(route.firstChild);
    }
    return false;
  }

  initI18n() {
    this.translate.addLangs([FR, EN]);
    // this language will be used as a fallback when a translation isn't found in the current language
    this.translate.setDefaultLang(FR);

    // the lang to use, if the lang isn't available, it will use the current loader to get them
    this.translate.use(FR);
  }

  idleTimeout() {
    this.idleService.idleTimeout().subscribe(() => {
      if (this.authService.isLoggedIn()) {
        this.authService.logout();

        let summary = "";
        let detail = "";
        this.translate.get("modal.disconnected").subscribe((res: string) => {
          summary = res;
        });
        this.translate.get("modal.disconnectedDetail").subscribe((res: string) => {
          detail = res;
        });

        this.messageService.add({
          severity: "info",
          summary: summary,
          detail: detail,
          life: 5000,
        });
        this.idleService.idleWatch();
      }
    });
  }

  @HostListener("window:storage", ["$event"])
  onStorageChange(event: StorageEvent) {
    if (event.key === TOKEN && event.oldValue !== null && event.newValue === null) {
      this.authService.logout();
    }
  }

  shouldShowContactPhone(): boolean {
    return !!localStorage.getItem(TOKEN);
  }

  onAdvisorSessionExpiredConfirm(): void {
    this.showAdvisorSessionExpiredDialog = false;
    this.authService.logout();
  }

  private initializeTranslation() {
    this.primengConfig.setTranslation({
      dayNamesMin: FRENCH_DAY_NAMES_MIN,
      monthNames: FRENCH_MONTH_NAMES,
    });
  }

  private listenToAdvisorSessionExpired(): void {
    this.advisorSessionExpiredService.expired$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.advisorSessionExpiredMessage = {
        header: this.translate.instant("advisor.sessionExpiredTitle"),
        message: this.translate.instant("advisor.sessionExpiredMessage"),
      };
      this.showAdvisorSessionExpiredDialog = true;
    });
  }
}
